import { Fragment, useEffect, useState } from 'react';

import { ApiClient, ApiError } from '../api/client';
import type { InstallationDetail, Settlement } from '../api/types';
import { formatToman, toJalaliDate, toPersianDigits } from '../format';

/* Pending settlement declarations, and the decision on each.
 *
 * Rejection demands a reason — the server enforces it too. "رد شد" with no
 * explanation produces a phone call every single time, and the installer has no
 * way to fix whatever was wrong. */

interface Props {
  api: ApiClient;
  onAuthLost: () => void;
}

export function SettlementsScreen({ api, onAuthLost }: Props) {
  const [rows, setRows] = useState<Settlement[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [rejecting, setRejecting] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  /** Which settlement's installations are open. One at a time. */
  const [expanded, setExpanded] = useState<number | null>(null);

  const guard = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      if (e instanceof ApiError && e.isUnauthenticated) return onAuthLost();
      setError(e instanceof Error ? e.message : 'خطای ناشناخته');
    }
  };

  const load = () => guard(async () => setRows(await api.pendingSettlements()));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = (id: number) =>
    guard(async () => {
      setBusyId(id);
      try {
        await api.approveSettlement(id);
        await load();
      } finally {
        setBusyId(null);
      }
    });

  const reject = (id: number) =>
    guard(async () => {
      setBusyId(id);
      try {
        await api.rejectSettlement(id, reason.trim());
        setRejecting(null);
        setReason('');
        await load();
      } finally {
        setBusyId(null);
      }
    });

  if (rows === null && !error) return <div className="card muted">در حال بارگذاری…</div>;

  return (
    <div>
      {error && <div className="alert error">{error}</div>}

      {rows && rows.length === 0 && (
        <div className="card muted center">تسویه‌ای در انتظار تایید نیست.</div>
      )}

      {rows && rows.length > 0 && (
        <div className="card">
          <table>
            <thead>
              <tr>
                <th>شناسه</th>
                <th>نصاب</th>
                <th>مبلغ</th>
                <th>تاریخ اعلام</th>
                <th>یادداشت</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => (
                <Fragment key={s.id}>
                  <tr>
                    <td className="mono">{toPersianDigits(s.id)}</td>
                    <td>{s.installerName ?? `#${toPersianDigits(s.installerId)}`}</td>
                    <td>{formatToman(s.amount)}</td>
                    <td>{toJalaliDate(s.createdAt)}</td>
                    <td className="muted">{s.note ?? '—'}</td>
                    <td>
                      <div className="row">
                        {/* Approving a sum without being able to see what is in
                            it is approving a number. The installations behind
                            this settlement are one click away rather than in
                            another tab that has to be cross-referenced by eye. */}
                        <button onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                          {expanded === s.id ? 'بستن' : 'نصب‌ها'}
                        </button>
                        <button
                          className="primary"
                          disabled={busyId === s.id}
                          onClick={() => void approve(s.id)}
                        >
                          تایید
                        </button>
                        <button
                          className="danger"
                          disabled={busyId === s.id}
                          onClick={() => {
                            setRejecting(rejecting === s.id ? null : s.id);
                            setReason('');
                          }}
                        >
                          رد
                        </button>
                      </div>
                    </td>
                  </tr>
                  {expanded === s.id && (
                    <tr>
                      <td colSpan={6}>
                        <SettlementInstallations api={api} settlementId={s.id} onAuthLost={onAuthLost} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rejecting !== null && (
        <div className="card">
          <label>
            <span>دلیل رد کردن تسویه {toPersianDigits(rejecting)} — برای نصاب نمایش داده می‌شود</span>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="مثلاً: مبلغ واریزی کمتر از مجموع نصب‌های انتخاب‌شده بود"
              autoFocus
            />
          </label>
          <div className="row">
            <button
              className="danger"
              disabled={reason.trim().length < 3 || busyId === rejecting}
              onClick={() => void reject(rejecting)}
            >
              رد کردن
            </button>
            <button onClick={() => setRejecting(null)}>انصراف</button>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The installations inside one settlement.
 *
 * Loaded on demand rather than with the list: most settlements are approved
 * without anyone needing to look, and fetching every line of every declaration
 * up front would make the screen slow for the common case to serve the rare one.
 */
function SettlementInstallations({
  api,
  settlementId,
  onAuthLost,
}: {
  api: ApiClient;
  settlementId: number;
  onAuthLost: () => void;
}) {
  const [rows, setRows] = useState<InstallationDetail[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** The server answered, but not with rows belonging to this settlement. */
  const [stale, setStale] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .installations({ settlementId, limit: 200 })
      .then((page) => {
        if (!alive) return;
        /* Filtered again on this side.
         *
         * The server does the filtering, but an OLDER server silently ignores a
         * query parameter it does not know — zod strips unknown keys — and hands
         * back every installation the installer has ever done. On a screen where
         * someone is about to approve a payment, showing more rows than belong
         * to it is the dangerous direction to be wrong in. If nothing survives
         * this, say so rather than showing an empty box. */
        const mine = page.rows.filter((r) => r.settlementId === settlementId);
        setStale(mine.length === 0 && page.rows.length > 0);
        setRows(mine);
      })
      .catch((err) => {
        if (!alive) return;
        if (err instanceof ApiError && err.isUnauthenticated) return onAuthLost();
        setError(err instanceof ApiError ? err.message : 'خطای ناشناخته');
      });
    /* The admin can close and reopen this faster than the request returns, and a
       late response landing in an unmounted row is a React warning at best. */
    return () => {
      alive = false;
    };
  }, [api, settlementId, onAuthLost]);

  if (error) return <div className="alert error">{error}</div>;
  if (rows === null) return <div className="muted">در حال بارگذاری…</div>;
  if (stale)
    return (
      <div className="alert warning">
        سرور نصب‌های این تسویه را جدا نکرد — نسخه‌ی بک‌اند قدیمی است. تا به‌روزرسانی، از تب
        «نصب‌ها» استفاده کنید.
      </div>
    );
  if (rows.length === 0) return <div className="muted">هیچ نصبی در این تسویه نیست.</div>;

  return (
    <table className="grid">
      <thead>
        <tr>
          <th>تاریخ</th>
          <th>نوع</th>
          <th>خودرو</th>
          <th>مالک</th>
          <th>کل</th>
          <th>سهم نصاب</th>
          <th>سهم ما</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>{toJalaliDate(row.createdAt)}</td>
            <td>
              {TYPE_LABEL[row.type]}
              {row.completedAt === null && <div className="badge warning">ناتمام</div>}
            </td>
            <td>
              <div>{row.device.modelTitle ?? '—'}</div>
              <div className="muted" dir="ltr">
                {row.device.deviceCode}
              </div>
              {row.device.vin && (
                <div className="muted" dir="ltr">
                  {row.device.vin}
                </div>
              )}
            </td>
            <td>
              <div>{row.owner?.fullName ?? '—'}</div>
              {row.owner && (
                <div className="muted" dir="ltr">
                  {toPersianDigits(row.owner.phone)}
                </div>
              )}
            </td>
            <td>{row.customerPrice ? formatToman(row.customerPrice) : '—'}</td>
            <td>{row.installerShare ? formatToman(row.installerShare) : '—'}</td>
            <td>{formatToman(row.amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

const TYPE_LABEL: Record<InstallationDetail['type'], string> = {
  INITIAL: 'نصب اولیه',
  RENEWAL: 'تمدید',
  REINSTALL: 'نصب مجدد',
};
